import React from 'react';
import { AlertTriangle, ImageIcon } from 'lucide-react';
import { YearbookElement, cornerRadiiToCss, elementShadowToCss } from '@/types/yearbook';
import SvgSticker from './SvgSticker';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ensureFontLoaded, fontAllowsBold, fontAllowsItalic } from '@/lib/fonts';

const ReadOnlyElement: React.FC<{ element: YearbookElement }> = ({ element }) => {
  const offset = element.imageOffset || { x: 50, y: 50 };

  if (element.type === 'text') ensureFontLoaded(element.textStyle?.fontFamily);

  const zIndex = element.zIndex != null ? element.zIndex : (element.zPriority === 'front' ? 10 : element.zPriority === 'back' ? 0 : 1);

  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: element.position.x,
    top: element.position.y,
    width: element.size.width,
    height: element.size.height,
    transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
    pointerEvents: 'none',
    backgroundColor: element.backgroundColor || undefined,
    zIndex,
    border: element.borderColor ? `1px solid ${element.borderColor}` : undefined,
    boxShadow: elementShadowToCss(element.shadow),
  };

  if (element.type === 'text') {
    const hasForbidden = element.forbiddenWords && element.forbiddenWords.length > 0;
    const textNode = (
      <div
        style={{
          ...baseStyle,
          fontSize: element.textStyle?.fontSize,
          fontFamily: element.textStyle?.fontFamily,
          fontWeight: fontAllowsBold(element.textStyle?.fontFamily) ? element.textStyle?.fontWeight : '400',
          color: element.textStyle?.color,
          textAlign: element.textStyle?.textAlign,
          fontStyle: element.textStyle?.italic && fontAllowsItalic(element.textStyle?.fontFamily) ? 'italic' : undefined,
          textDecoration: element.textStyle?.underline ? 'underline' : undefined,
          overflow: 'hidden',
          whiteSpace: 'pre-wrap',
          lineHeight: 1.4,
          outline: hasForbidden ? '2px dashed hsl(var(--destructive))' : undefined,
          outlineOffset: hasForbidden ? '2px' : undefined,
        }}
      >
        {element.content}
      </div>
    );

    if (!hasForbidden) return textNode;

    return (
      <div className="relative" style={{ position: 'absolute', left: element.position.x, top: element.position.y, width: element.size.width, height: element.size.height, zIndex: baseStyle.zIndex }}>
        {textNode}
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="absolute -top-2 -right-2 flex items-center justify-center rounded-full shadow-md pointer-events-auto cursor-help hover:scale-110 transition-transform"
              style={{
                width: 18,
                height: 18,
                backgroundColor: 'hsl(var(--destructive))',
                color: 'hsl(var(--destructive-foreground))',
                zIndex: 2,
                border: 'none',
                padding: 0,
              }}
              aria-label={`Mots interdits détectés : ${element.forbiddenWords?.join(', ')}`}
            >
              <AlertTriangle size={12} strokeWidth={2.5} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} className="max-w-xs border-destructive/30 bg-popover/95 backdrop-blur-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">Mots interdits détectés</p>
                <p className="text-xs text-muted-foreground mt-1">{element.forbiddenWords?.join(', ')}</p>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }


  // Images (including stickers and template_stickers)
  if (element.type === 'image') {
    const src = element.imageUrl || element.thumbnail;
    if (!src) return null;
    const isSvgSticker = element.isSticker && (element.stickerFormat === 'svg' || element.stickerFormat === 'simple_svg');
    return (
      <div style={{ ...baseStyle, borderRadius: cornerRadiiToCss(element.cornerRadii) || (element.isCircle ? '50%' : element.borderRadius ? `${element.borderRadius}px` : undefined) }} className="overflow-hidden rounded-sm">
        {isSvgSticker ? (
          <SvgSticker url={src} color={element.stickerColor} />
        ) : (
          <img
            src={src}
            alt=""
            className="w-full h-full"
            style={{ objectFit: element.forceDimension ? 'fill' : 'cover', objectPosition: `${offset.x}% ${offset.y}%` }}
            draggable={false}
          />
        )}
      </div>
    );
  }

  if (element.type === 'free_slot') {
    const circleStyle = element.isCircle ? { borderRadius: '50%' } : cornerRadiiToCss(element.cornerRadii) ? { borderRadius: cornerRadiiToCss(element.cornerRadii) } : {};
    return (
      <div
        style={{ ...baseStyle, ...circleStyle }}
        className="border-2 border-dashed border-muted-foreground/40 flex items-center justify-center"
      >
        <ImageIcon size={24} className="text-muted-foreground/40" />
      </div>
    );
  }

  if (element.type === 'slot') {
    const slotSrc = element.previewImageUrl || element.imageUrl;
    const circleStyle = element.isCircle ? { borderRadius: '50%' } : cornerRadiiToCss(element.cornerRadii) ? { borderRadius: cornerRadiiToCss(element.cornerRadii) } : element.borderRadius ? { borderRadius: `${element.borderRadius}px` } : {};
    if (slotSrc) {
      return (
        <div style={{ ...baseStyle, ...circleStyle }} className="overflow-hidden">
          <img
            src={slotSrc}
            alt=""
            className="w-full h-full"
            style={{
              objectFit: 'cover',
              objectPosition: `${offset.x}% ${offset.y}%`,
              transform: element.imageScale && element.imageScale > 1 ? `scale(${element.imageScale})` : undefined,
              transformOrigin: `${offset.x}% ${offset.y}%`,
            }}
            draggable={false}
          />
        </div>
      );
    }
    return (
      <div
        className="border-2 border-dashed border-muted-foreground/50 rounded-sm flex items-center justify-center"
        style={{ ...baseStyle, ...circleStyle, backgroundColor: 'rgba(255,255,255,0.15)' }}
      >
        <ImageIcon
          style={{ width: Math.max(10, Math.min(element.size.width, element.size.height) * 0.3), height: 'auto', opacity: 0.5 }}
          className="text-muted-foreground"
          strokeWidth={1.5}
        />
      </div>
    );
  }

  if (element.type === 'shape') {
    const color = element.textStyle?.color || '#000000';
    const w = element.size.width;
    const h = element.size.height;
    return (
      <div style={{ ...baseStyle, borderRadius: cornerRadiiToCss(element.cornerRadii) || undefined }}>
        <svg width={w} height={h}>
          {element.shapeType === 'circle' && (
            <ellipse cx={w / 2} cy={h / 2} rx={w / 2} ry={h / 2} fill={color} />
          )}
          {element.shapeType === 'line' && (
            <line x1={0} y1={h / 2} x2={w} y2={h / 2} stroke={color} strokeWidth={element.strokeWidth ?? 2} />
          )}
          {(!element.shapeType || element.shapeType === 'rect') && (
            <rect width={w} height={h} fill={color} />
          )}
        </svg>
      </div>
    );
  }

  if (element.type === 'shape_border') {
    return (
      <div
        style={{
          ...baseStyle,
          border: `2px solid ${element.textStyle?.color || '#000000'}`,
          borderRadius: cornerRadiiToCss(element.cornerRadii) || undefined,
        }}
      />
    );
  }

  return null;
};

export default ReadOnlyElement;
